import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Settings as SettingsIcon, Menu, ChevronLeft, ChevronRight, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ErrorState } from "../components/shared/ErrorState";
import { Skeleton } from "../components/ui/skeleton";
import { SixBySixSettingsDialog, SIXBYSIX_DEFAULT_LAYOUT } from "../components/routes/SixBySixSettingsDialog";
import { AverySettingsDialog } from "../components/routes/AverySettingsDialog";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";

// Avery 5160: US Letter, 30 labels/sheet (3 cols x 10 rows), each 2.625in x 1in,
// 0.5in top/bottom margin, 0.1875in side margins, 0.125in gap between columns,
// no gap between rows. https://www.avery.com/templates/5160
const AVERY_5160_PER_SHEET = 30;
const PRINT_STYLE_STORAGE_KEY = "route_stickers_print_style";
const THUMBNAIL_PX_PER_IN = 20;

function useIsMobile() {
  const [mobile, setMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 640 : false));
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

// Measures the canvas pane's own content-box size so the sheet can be scaled to
// actually FIT inside it (instead of a fixed guess that's too small or clips off).
// Uses a state-based callback ref (not useRef) so the observer correctly re-attaches
// once the canvas div actually mounts (it's behind a loading/error gate on first render).
function useElementSize() {
  const [node, setNode] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [node]);
  return [setNode, size];
}

// Splits stops into two equal(ish) halves by stop order and pairs them one-per-sheet,
// so cutting every sheet in half and stacking each pile leaves it already in delivery
// order. Any odd stop out is always the very last stop of the whole route.
function pairForSixBySix(deliveries) {
  const n = deliveries.length;
  const halfA = deliveries.slice(0, Math.floor(n / 2));
  const halfB = deliveries.slice(Math.floor(n / 2));
  const sheetCount = Math.max(halfA.length, halfB.length);
  const sheets = [];
  for (let i = 0; i < sheetCount; i++) {
    sheets.push({ sticker1: halfA[i] || null, sticker2: halfB[i] || null });
  }
  return sheets;
}

export default function RouteStickers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, route: null });
  const [printStyle, setPrintStyle] = useState(() => localStorage.getItem(PRINT_STYLE_STORAGE_KEY) || "avery");
  const [layout, setLayout] = useState(SIXBYSIX_DEFAULT_LAYOUT);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [averySettingsOpen, setAverySettingsOpen] = useState(false);
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [canvasRef, canvasSize] = useElementSize();
  // All sheets are stacked & rendered together in the main pane so the whole
  // route can be reviewed just by scrolling — sheetRefsMap lets nav controls
  // ("Page N" input, prev/next, thumbnail click) smooth-scroll straight to a
  // given sheet's position inside that same scroll container.
  const sheetRefsMap = React.useRef({});
  const mainCanvasNodeRef = React.useRef(null);
  const setCanvasRef = (el) => {
    mainCanvasNodeRef.current = el;
    canvasRef(el);
  };

  useEffect(() => {
    apiClient.get(`/routes/${id}`)
      .then(({ data }) => setState({ loading: false, error: null, route: data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load this route."), route: null }));
  }, [id]);

  useEffect(() => {
    apiClient.get("/outlet/sticker-layout").then(({ data }) => setLayout(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSheetIndex(0);
    mainCanvasNodeRef.current?.scrollTo({ top: 0 });
  }, [printStyle, id]);

  const selectPrintStyle = (style) => {
    setPrintStyle(style);
    localStorage.setItem(PRINT_STYLE_STORAGE_KEY, style);
  };

  // Avery needs a zero page margin so our own inch-perfect measurements aren't
  // thrown off by the app's default print margin; the 6x6 style needs a 6x6in
  // custom page size. Both scoped to only while this page is mounted so no
  // other print page (reports, etc.) is affected.
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = printStyle === "sixbysix"
      ? '@media print { @page { size: 6in 6in; margin: 0; } }'
      : '@media print { @page { size: letter; margin: 0; } }';
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [printStyle]);

  const saveLayout = async (draft) => {
    try {
      const { data } = await apiClient.put("/outlet/sticker-layout", draft);
      setLayout(data);
      setSettingsOpen(false);
      setAverySettingsOpen(false);
      toast.success("Sticker sheet settings saved");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not save these settings."));
    }
  };

  if (state.loading) {
    return (
      <div className="p-8" data-testid="route-stickers-page">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    );
  }

  if (state.error || !state.route) {
    return (
      <div className="p-8" data-testid="route-stickers-page">
        <ErrorState message={state.error || "Route not found."} onRetry={() => navigate(`/routes/${id}`)} />
      </div>
    );
  }

  const r = state.route;
  const total = r.deliveries.length;

  // Every real stop always prints its own unchanged sticker (name + Route/Tiffin #).
  // One EXTRA sticker for the driver is appended after all real stops — name spot
  // shows the driver's name, second line shows just the route number (no tiffin #).
  const driverItem = { id: "driver", isDriverSticker: true, customer_name: `Delivery By- ${r.driver_name || "Unassigned"}` };

  const stickerMeta = (d, idx) => {
    if (d.isDriverSticker) return `Route- ${r.id}`;
    return `Route- ${r.id}, Tiffin- ${d.delivery_order_id ?? idx + 1}`;
  };

  const averyItems = total > 0 ? [...r.deliveries, driverItem] : [];
  const averySheets = [];
  for (let i = 0; i < averyItems.length; i += AVERY_5160_PER_SHEET) {
    averySheets.push(averyItems.slice(i, i + AVERY_5160_PER_SHEET));
  }

  // Real-stop pairing is computed exactly as before (untouched); the driver sticker
  // then fills the last sheet's empty slot (odd stop count) or gets its own new sheet.
  const sixSheets = pairForSixBySix(r.deliveries);
  if (total > 0) {
    const lastSheet = sixSheets[sixSheets.length - 1];
    if (lastSheet && !lastSheet.sticker1) {
      lastSheet.sticker1 = driverItem;
    } else if (lastSheet && !lastSheet.sticker2) {
      lastSheet.sticker2 = driverItem;
    } else {
      sixSheets.push({ sticker1: driverItem, sticker2: null });
    }
  }

  const sheets = printStyle === "avery" ? averySheets : sixSheets;
  const totalSheets = sheets.length;

  const renderAverySheet = (sheet, sheetIdx) => (
    <div
      key={sheetIdx}
      className="avery-5160-sheet"
      style={{ pageBreakAfter: sheetIdx < averySheets.length - 1 ? "always" : "auto" }}
      data-testid={`route-stickers-avery-sheet-${sheetIdx}`}
    >
      {sheet.map((d, i) => (
        <div key={d.id} className="avery-5160-label" data-testid={`route-sticker-print-${d.id}`}>
          <p className="avery-5160-name" style={{ fontSize: `${layout.avery_name_font_pt}pt` }}>{d.customer_name || `Customer #${d.customer_id}`}</p>
          <p className="avery-5160-meta" style={{ fontSize: `${layout.avery_meta_font_pt}pt`, marginTop: `${layout.avery_line_gap_pt}pt` }}>{stickerMeta(d, sheetIdx * AVERY_5160_PER_SHEET + i)}</p>
        </div>
      ))}
    </div>
  );

  const renderSixBySixSheet = (sheet, sheetIdx, forPrint, pxPerIn) => (
    <div
      key={sheetIdx}
      className={forPrint ? "sixbysix-sheet" : "sixbysix-sheet sixbysix-sheet--screen"}
      style={forPrint
        ? { pageBreakAfter: sheetIdx < sixSheets.length - 1 ? "always" : "auto" }
        : { width: 6 * pxPerIn, height: 6 * pxPerIn, background: "#ffffff", overflow: "hidden" }}
      data-testid={`route-stickers-sixbysix-sheet-${sheetIdx}`}
    >
      {[["sticker1", sheet.sticker1], ["sticker2", sheet.sticker2]].map(([key, d]) =>
        d ? (
          <div
            key={key}
            className="sixbysix-sticker"
            style={forPrint ? {
              left: `${layout[`${key}_left_in`]}in`,
              top: `${layout[`${key}_top_in`]}in`,
              width: `${layout.width_in}in`,
              height: `${layout.height_in}in`,
            } : {
              left: layout[`${key}_left_in`] * pxPerIn,
              top: layout[`${key}_top_in`] * pxPerIn,
              width: layout.width_in * pxPerIn,
              height: layout.height_in * pxPerIn,
              padding: `${0.12 * pxPerIn}px ${0.16 * pxPerIn}px`,
            }}
            data-testid={forPrint ? `route-sticker-print-${d.id}` : `route-sticker-${d.id}`}
          >
            <p
              className="avery-5160-name"
              style={forPrint
                ? { fontSize: `${layout.sixbysix_name_font_pt}pt` }
                : { fontSize: (layout.sixbysix_name_font_pt / 72) * pxPerIn, color: "#0f172a" }}
            >
              {d.customer_name || `Customer #${d.customer_id}`}
            </p>
            <p
              className="avery-5160-meta"
              style={forPrint
                ? { fontSize: `${layout.sixbysix_meta_font_pt}pt`, marginTop: `${layout.sixbysix_line_gap_pt}pt` }
                : { fontSize: (layout.sixbysix_meta_font_pt / 72) * pxPerIn, marginTop: (layout.sixbysix_line_gap_pt / 72) * pxPerIn }}
            >
              {stickerMeta(d, r.deliveries.indexOf(d))}
            </p>
          </div>
        ) : null
      )}
    </div>
  );

  // Scaled-for-screen Avery preview (thumbnail + zoomable main pane) — mirrors the
  // exact print geometry (3 cols x 2.625in, 10 rows x 1in, 0.125in column gap,
  // 0.5in top margin) but in px-per-inch so it can shrink/zoom freely on screen.
  const renderAveryPreviewSheet = (sheet, sheetIdx, pxPerIn) => {
    const labelW = 2.625 * pxPerIn, labelH = 1 * pxPerIn, colGap = 0.125 * pxPerIn, topMargin = 0.5 * pxPerIn;
    const ptToPx = (pt) => (pt / 72) * pxPerIn;
    return (
      <div
        className="relative overflow-hidden bg-white shadow-2xl"
        style={{ width: 8.5 * pxPerIn, height: 11 * pxPerIn }}
        data-testid={`route-stickers-avery-preview-sheet-${sheetIdx}`}
      >
        <div
          className="absolute"
          style={{ left: 0.1875 * pxPerIn, top: topMargin, display: "grid", gridTemplateColumns: `repeat(3, ${labelW}px)`, gridAutoRows: `${labelH}px`, columnGap: `${colGap}px` }}
        >
          {sheet.map((d, i) => (
            <div
              key={d.id}
              className="overflow-hidden border border-dashed border-slate-300 bg-white"
              style={{ width: labelW, height: labelH, padding: `${pxPerIn * 0.12}px ${pxPerIn * 0.16}px`, display: "flex", flexDirection: "column" }}
              data-testid={`route-sticker-${d.id}`}
            >
              <p
                className="text-slate-950"
                style={{ margin: 0, fontWeight: 700, fontSize: ptToPx(layout.avery_name_font_pt), lineHeight: 1.2, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                data-testid={`route-sticker-customer-${d.id}`}
              >
                {d.customer_name || `Customer #${d.customer_id}`}
              </p>
              <p
                className="text-slate-600"
                style={{ margin: 0, marginTop: ptToPx(layout.avery_line_gap_pt), fontSize: ptToPx(layout.avery_meta_font_pt), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                data-testid={`route-sticker-position-${d.id}`}
              >
                {stickerMeta(d, sheetIdx * AVERY_5160_PER_SHEET + i)}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPreviewSheet = (sheet, sheetIdx, pxPerIn) => (
    printStyle === "avery" ? renderAveryPreviewSheet(sheet, sheetIdx, pxPerIn) : renderSixBySixSheet(sheet, sheetIdx, false, pxPerIn)
  );

  const sheetWidthIn = printStyle === "avery" ? 8.5 : 6;
  const sheetHeightIn = printStyle === "avery" ? 11 : 6;
  // Avery: "100%" zoom = fit the whole sheet inside the canvas pane (like a real PDF
  // viewer's default view). 6x6: always shown at true full size (96px/in, matching a
  // real inch on screen) — the canvas pane scrolls to reveal the rest since a 6x6in
  // sheet is usually taller than the visible pane.
  const fitPxPerIn = printStyle === "sixbysix"
    ? 96
    : (canvasSize.width > 0 && canvasSize.height > 0
      ? Math.max(6, Math.min((canvasSize.width * 0.92) / sheetWidthIn, (canvasSize.height * 0.92) / sheetHeightIn))
      : (isMobile ? 22 : 60));
  const mainPxPerIn = fitPxPerIn * (zoom / 100);

  const goToSheet = (i) => {
    const clamped = Math.max(0, Math.min(totalSheets - 1, i));
    setSheetIndex(clamped);
    const container = mainCanvasNodeRef.current;
    const el = sheetRefsMap.current[clamped];
    if (!container || !el) return;
    // Scroll only the inner canvas pane (not scrollIntoView, which bubbles
    // and scrolls ancestor containers too, cutting off the fixed header).
    const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTo({ top: Math.max(0, offset - 16), behavior: "smooth" });
  };

  return (
    <div className="p-2 md:p-4" data-testid="route-stickers-page">
      {total === 0 ? (
        <>
          <div className="mb-4 flex items-center gap-3 print:hidden">
            <Button variant="secondary" onClick={() => navigate(`/routes/${id}`)} data-testid="route-stickers-back-button">
              <ArrowLeft size={16} strokeWidth={1.5} /> Back
            </Button>
            <h1 className="font-heading text-lg font-semibold text-text-main">Route #{r.id} — Stickers (0)</h1>
          </div>
          <p className="text-sm text-text-muted print:hidden">This route has no stops to make stickers for.</p>
        </>
      ) : (
        <div
          className="flex h-[92vh] min-h-[680px] flex-col overflow-hidden rounded-xl border border-slate-800 bg-slate-950 text-slate-100 shadow-2xl print:hidden"
          data-testid="route-stickers-viewer"
        >
          {/* Top bar */}
          <div className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-800 bg-slate-900 px-3 py-2 md:px-4" data-testid="route-stickers-top-bar">
            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={() => navigate(`/routes/${id}`)}
                className="rounded-md p-1.5 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="route-stickers-back-button"
              >
                <ArrowLeft size={18} strokeWidth={1.75} />
              </button>
              <Printer size={18} strokeWidth={1.75} className="text-accent" />
              <div className="leading-tight">
                <p className="font-heading text-sm font-semibold text-white">Label Preview</p>
                <p className="hidden text-[11px] text-slate-400 sm:block">Route #{r.id} · {total} stops · {r.driver_name || "Unassigned"}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex rounded-full border border-slate-700 bg-slate-800/70 p-1" data-testid="route-stickers-style-toggle">
                <button
                  type="button"
                  onClick={() => selectPrintStyle("avery")}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${printStyle === "avery" ? "bg-accent text-primary" : "text-slate-300 hover:text-white"}`}
                  data-testid="route-stickers-style-avery"
                >
                  Avery&reg; 5160
                </button>
                <button
                  type="button"
                  onClick={() => selectPrintStyle("sixbysix")}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${printStyle === "sixbysix" ? "bg-accent text-primary" : "text-slate-300 hover:text-white"}`}
                  data-testid="route-stickers-style-sixbysix"
                >
                  6&times;6&quot; Two-Up
                </button>
              </div>

              {printStyle === "avery" ? (
                <button
                  onClick={() => setAverySettingsOpen(true)}
                  className="rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white"
                  data-testid="route-stickers-avery-settings-button"
                  title="Sticker settings"
                >
                  <SettingsIcon size={17} strokeWidth={1.75} />
                </button>
              ) : (
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white"
                  data-testid="route-stickers-settings-button"
                  title="Sticker settings"
                >
                  <SettingsIcon size={17} strokeWidth={1.75} />
                </button>
              )}
              <Button variant="accent" onClick={() => window.print()} data-testid="route-stickers-print-button">
                <Printer size={16} strokeWidth={1.5} /> Print
              </Button>
            </div>
          </div>

          {/* Controls toolbar */}
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-800 bg-slate-900/90 px-3 py-2 backdrop-blur-md md:px-4" data-testid="route-stickers-controls-toolbar">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="rounded-md p-1.5 text-slate-300 hover:bg-slate-800 hover:text-white"
              data-testid="route-stickers-sidebar-toggle"
              title="Toggle sheet list"
            >
              <Menu size={16} strokeWidth={1.75} />
            </button>

            <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 px-2 py-1">
              <button
                onClick={() => goToSheet(sheetIndex - 1)}
                disabled={sheetIndex === 0}
                className="rounded p-1 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30"
                data-testid="route-stickers-prev-sheet"
              >
                <ChevronLeft size={15} strokeWidth={2} />
              </button>
              <input
                type="number" min={1} max={totalSheets} value={sheetIndex + 1}
                onChange={(e) => goToSheet(Number(e.target.value) - 1)}
                className="w-10 rounded bg-slate-800 px-1 py-0.5 text-center text-xs text-white outline-none"
                data-testid="route-stickers-sheet-input"
              />
              <span className="text-xs text-slate-400">/ {totalSheets}</span>
              <button
                onClick={() => goToSheet(sheetIndex + 1)}
                disabled={sheetIndex >= totalSheets - 1}
                className="rounded p-1 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30"
                data-testid="route-stickers-next-sheet"
              >
                <ChevronRight size={15} strokeWidth={2} />
              </button>
            </div>

            <div className="hidden h-4 w-px bg-slate-700 sm:block" />

            <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 px-2 py-1">
              <button
                onClick={() => setZoom((z) => Math.max(50, z - 25))}
                className="rounded p-1 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="route-stickers-zoom-out"
              >
                <Minus size={14} strokeWidth={2} />
              </button>
              <span className="w-10 text-center text-xs text-slate-300" data-testid="route-stickers-zoom-level">{zoom}%</span>
              <button
                onClick={() => setZoom((z) => Math.min(200, z + 25))}
                className="rounded p-1 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="route-stickers-zoom-in"
              >
                <Plus size={14} strokeWidth={2} />
              </button>
            </div>

            <p className="ml-auto hidden text-[11px] text-slate-400 md:block" data-testid="route-stickers-print-hint">
              {printStyle === "avery"
                ? 'Avery\u00ae 5160 sheets (30 labels/sheet).'
                : '2 stickers per 6\u00d76" sheet, split so cut/stacked piles come out in order.'}
              {" "}Set print Margins to "None" and Scale to "100%".
            </p>
          </div>

          {/* Split view */}
          <div className="flex flex-1 overflow-hidden">
            {sidebarOpen && (
              <div className="w-40 shrink-0 overflow-y-auto border-r border-slate-800 bg-slate-900/95 p-3 md:w-56" data-testid="route-stickers-thumbnail-sidebar">
                <div className="flex flex-col gap-3">
                  {sheets.map((sheet, i) => (
                    <div
                      key={i}
                      role="button" tabIndex={0}
                      onClick={() => goToSheet(i)}
                      onKeyDown={(e) => e.key === "Enter" && goToSheet(i)}
                      className={`group relative cursor-pointer rounded-lg border-2 p-2 text-left transition-all hover:scale-[1.02] ${i === sheetIndex ? "border-accent bg-accent/10 shadow-lg shadow-accent/10" : "border-slate-700/60 bg-slate-800/60 hover:border-slate-500"}`}
                      data-testid={`route-stickers-thumbnail-${i}`}
                    >
                      <div className="flex items-center justify-center overflow-hidden">
                        {renderPreviewSheet(sheet, i, THUMBNAIL_PX_PER_IN)}
                      </div>
                      <p className={`mt-1.5 text-center text-[10px] font-semibold ${i === sheetIndex ? "text-accent" : "text-slate-500"}`}>{i + 1}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div ref={setCanvasRef} className="flex min-w-0 flex-1 flex-col items-center gap-8 overflow-auto bg-slate-950 p-6" data-testid="route-stickers-main-canvas">
              {sheets.map((sheet, i) => (
                <div
                  key={i}
                  ref={(el) => { sheetRefsMap.current[i] = el; }}
                  className="flex flex-col items-center gap-2"
                  data-testid={`route-stickers-canvas-sheet-${i}`}
                >
                  <span className={`text-[11px] font-semibold ${i === sheetIndex ? "text-accent" : "text-slate-500"}`}>
                    Page {i + 1} of {totalSheets}
                  </span>
                  {renderPreviewSheet(sheet, i, mainPxPerIn)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Print output — exact sheet geometry for the selected style (unaffected by the on-screen viewer above) */}
      {total > 0 && (
        printStyle === "avery" ? (
          <div className="hidden print:block" data-testid="route-stickers-avery-sheets">
            {averySheets.map((sheet, sheetIdx) => renderAverySheet(sheet, sheetIdx))}
          </div>
        ) : (
          <div className="hidden print:block" data-testid="route-stickers-sixbysix-sheets">
            {sixSheets.map((sheet, sheetIdx) => renderSixBySixSheet(sheet, sheetIdx, true))}
          </div>
        )
      )}

      <SixBySixSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} layout={layout} onSave={saveLayout} />
      <AverySettingsDialog open={averySettingsOpen} onOpenChange={setAverySettingsOpen} layout={layout} onSave={saveLayout} />
    </div>
  );
}
